// A household's recipe as stored (#1841), opened from the family card's Recipes and Meal plan tabs to debug what a
// customer sees, and edited on their behalf (#1843). adminGetFamilyRecipe reads it and adminSaveRecipeEdit saves an
// edit with the apps' own checks, both through the Admin SDK; the drawer markup is in templates/users.html.
// The *HTML functions and editRequest are pure, so tests/admin/familyRecipe.test.js can check them without a browser.

import { BADGE, BUTTON, FIELD, TABLE } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";
import { escapeHTML, safeUrl, SOURCE_LABELS } from "./familyDetailsPanel.js";
import { showDialog } from "./ui/dialog.js";

const MUTED = "text-sm text-gray-500 dark:text-gray-400";
const MONO = "font-mono text-xs text-gray-500 dark:text-gray-400";
const H = "text-sm font-semibold text-gray-900 dark:text-white";
const TD = "px-3 py-2 text-sm text-gray-500 dark:text-gray-400 align-top";

// The timelines a Smart recipe can have, in the apps' order; any other key under `instructions` follows them.
// Prep ahead stays a version of a Smart recipe, edited like the others; nothing in the apps cooks from it since #2451,
// and whether it stays is open (#2454).
const VARIANTS = [
  ["fastest_variant", "Fastest"],
  ["dish_saver_variant", "Fewest dishes"],
  ["prep_ahead_variant", "Prep ahead"]
];

const TYPE_BADGES = {
  smart_user: `<span class="${BADGE.paprika}">Smart</span>`,
  raw_user: `<span class="${BADGE.gray}">Draft</span>`
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function amountText(amount, unit) {
  if (typeof amount !== "number") return unit ? String(unit) : "";
  const n = Number.isInteger(amount) ? amount : Math.round(amount * 100) / 100;
  return `${n}${unit ? ` ${unit}` : ""}`;
}

function minutes(value) {
  return typeof value === "number" && value > 0 ? `${value} min` : "—";
}

function textLines(value) {
  if (!Array.isArray(value)) return [];
  return value.map((line) => (typeof line === "string" ? line : (line && (line.text || line.instruction || line.name)) || ""))
    .filter((line) => line.trim());
}

/** A member's name or email for a uid, from the family card's details; the uid when the member is unknown. */
function memberLabel(uid, members) {
  if (!uid) return "—";
  const m = (members || []).find((x) => x.uid === uid);
  const label = m ? (m.name || m.email) : null;
  return label ? `${escapeHTML(label)} <span class="${MONO}">${escapeHTML(uid)}</span>` : `<span class="${MONO}">${escapeHTML(uid)}</span>`;
}

function factsHTML(r, members) {
  const source = SOURCE_LABELS[r.source_type] || r.source_type || "—";
  const url = safeUrl(r.full_url) || safeUrl(r.source_url);
  const servings = r.servings ?? r.planning_metrics?.servings ?? r.base_servings_adults;
  const facts = [
    ["Source", escapeHTML(source)],
    ["Link", url ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(r.source_domain || new URL(url).hostname)}</a>` : "—"],
    ["Servings", servings ?? "—"],
    ["Prep", minutes(r.prep_time_minutes)],
    ["Cook", minutes(r.cook_time_minutes)],
    ["Total", minutes(r.total_time_minutes)],
    ["Created", escapeHTML(formatDate(r.created_at))],
    ["Updated", escapeHTML(formatDate(r.updated_at))],
    ["Created by", memberLabel(r.created_by, members)]
  ];
  if (r.imported_by) facts.push(["Imported by", memberLabel(r.imported_by, members)]);
  if (r.schema_version !== undefined && r.schema_version !== null) facts.push(["Schema", escapeHTML(r.schema_version)]);
  return `
    <dl class="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
      ${facts.map(([label, value]) => `<div class="min-w-0"><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">${label}</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${value}</dd></div>`).join("")}
    </dl>`;
}

/** The ingredients: a Smart recipe's lines with their catalog ids, or a draft's text as typed. */
export function ingredientsHTML(r) {
  if (Array.isArray(r.ingredients) && r.ingredients.length) {
    const rows = r.ingredients.map((item) => {
      if (typeof item === "string") return `<tr><td class="${TD}"></td><td class="${TD} text-gray-900 dark:text-white">${escapeHTML(item)}</td></tr>`;
      const i = item || {};
      const name = i.name_singular || i.display_name || i.name || i.raw_line || "Unnamed";
      const flags = [
        i.is_optional ? `<span class="${BADGE.gray}">Optional</span>` : "",
        i.is_staple ? `<span class="${BADGE.blue}">Staple</span>` : ""
      ].join(" ");
      return `
        <tr>
          <td class="${TD} whitespace-nowrap">${escapeHTML(amountText(i.amount, i.unit)) || "—"}</td>
          <td class="${TD}">
            <span class="font-medium text-gray-900 dark:text-white">${escapeHTML(name)}</span>${i.prep_note ? `, ${escapeHTML(i.prep_note)}` : ""} ${flags}
            ${i.ingredient_id ? `<div class="${MONO} mt-0.5 break-all">${escapeHTML(i.ingredient_id)}${i.category ? ` · ${escapeHTML(i.category)}` : ""}</div>` : ""}
          </td>
        </tr>`;
    }).join("");
    return `<div class="relative overflow-x-auto"><table class="${TABLE.table}"><tbody class="${TABLE.tbody}">${rows}</tbody></table></div>`;
  }
  const lines = textLines(r.ingredients_text_list);
  if (lines.length) {
    return `<ul class="list-disc space-y-1 pl-5 text-sm text-gray-900 dark:text-white">${lines.map((l) => `<li>${escapeHTML(l)}</li>`).join("")}</ul>`;
  }
  return `<p class="${MUTED}">None: no <code class="font-mono text-xs">ingredients</code> or <code class="font-mono text-xs">ingredients_text_list</code>.</p>`;
}

function stepsListHTML(steps) {
  const sorted = steps
    .map((s, idx) => (typeof s === "string" ? { step_number: idx + 1, instruction: s } : (s || {})))
    .sort((a, b) => (a.step_number ?? 0) - (b.step_number ?? 0));
  return `<ol class="space-y-2">${sorted.map((s, idx) => {
    const meta = [
      typeof s.duration_seconds === "number" ? `${Math.round(s.duration_seconds / 60)} min` : null,
      s.step_tag || null
    ].filter(Boolean).join(" · ");
    return `
      <li class="flex gap-3 text-sm">
        <span class="w-5 flex-none text-right font-mono text-xs leading-5 text-gray-400">${escapeHTML(s.step_number ?? idx + 1)}</span>
        <div class="min-w-0">
          <p class="text-gray-900 dark:text-white">${escapeHTML(s.instruction || s.instruction_text || s.text || "")}</p>
          ${meta ? `<p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">${escapeHTML(meta)}</p>` : ""}
        </div>
      </li>`;
  }).join("")}</ol>`;
}

/** The steps: one list per timeline a Smart recipe has, or a draft's text as typed. */
export function stepsHTML(r) {
  const instructions = r.instructions;
  if (Array.isArray(instructions) && instructions.length) return stepsListHTML(instructions);
  if (instructions && typeof instructions === "object") {
    const known = VARIANTS.filter(([key]) => instructions[key]);
    const others = Object.keys(instructions).filter((key) => !VARIANTS.some(([k]) => k === key)).map((key) => [key, key]);
    const sections = [...known, ...others]
      .map(([key, label]) => {
        const v = instructions[key];
        const steps = Array.isArray(v?.steps) ? v.steps : (Array.isArray(v) ? v : []);
        if (!steps.length) return "";
        const times = v && !Array.isArray(v) && typeof v.total_time_minutes === "number" ? ` · ${v.total_time_minutes} min` : "";
        return `
          <div>
            <h5 class="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">${escapeHTML(label)}${escapeHTML(times)} <span class="${MONO} normal-case">${escapeHTML(key)}</span></h5>
            <div class="mt-2">${stepsListHTML(steps)}</div>
          </div>`;
      })
      .filter(Boolean);
    if (sections.length) return `<div class="space-y-5">${sections.join("")}</div>`;
  }
  const lines = textLines(r.instructions_text_list);
  if (lines.length) return stepsListHTML(lines);
  return `<p class="${MUTED}">None: no <code class="font-mono text-xs">instructions</code> or <code class="font-mono text-xs">instructions_text_list</code>.</p>`;
}

function nutritionHTML(r) {
  const n = r.nutrition;
  if (!n || typeof n !== "object") {
    return `<p class="${MUTED}">${r.recipe_type === "smart_user" ? "Not worked out yet (onUserRecipeWritten writes it)." : "None: only Smart recipes get nutrition."}</p>`;
  }
  const coverage = typeof n.coverage === "number" ? `${Math.round(n.coverage * 100)} % of the lines counted` : "coverage unknown";
  const missing = Array.isArray(n.missing_ingredients) && n.missing_ingredients.length
    ? ` Not counted: ${n.missing_ingredients.map((m) => escapeHTML(m)).join(", ")}.`
    : "";
  return `<p class="${MUTED}">${n.per_serving ? "Per serving worked out" : "Too few lines to count"}, ${escapeHTML(coverage)}.${missing} The values are in the JSON below.</p>`;
}

/** The household's own recipes, Smart or draft, are the ones adminSaveRecipeEdit takes. */
export function isEditable(r) {
  return r?.recipe_type === "smart_user" || r?.recipe_type === "raw_user";
}

/** The CRM's earlier edits of this recipe (#1843), newest first. */
function adminEditsHTML(edits) {
  if (!Array.isArray(edits) || !edits.length) return "";
  const rows = edits.map((e) => `
    <li class="text-sm text-gray-700 dark:text-gray-300">
      ${escapeHTML(formatDate(e.edited_at))} · ${escapeHTML(e.admin_email || e.admin_uid || "an admin")}
      <span class="${MONO}">revision ${escapeHTML(e.edit_revision ?? "?")}</span>
      ${e.note ? `<div class="text-xs text-gray-500 dark:text-gray-400">${escapeHTML(e.note)}</div>` : ""}
    </li>`).join("");
  return `
    <div class="rounded-md bg-yellow-50 px-3 py-2 dark:bg-yellow-500/10">
      <p class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Edited in the CRM</p>
      <ul class="mt-1 space-y-1">${rows}</ul>
      <p class="mt-1 text-xs text-yellow-800/80 dark:text-yellow-200/70">Each edit keeps the recipe as it was, under <code class="font-mono">admin_recipe_edits</code>.</p>
    </div>`;
}

/** The drawer's body for an adminGetFamilyRecipe result; `members` names who created it. */
export function familyRecipeHTML(result, members = []) {
  if (!result?.found) {
    return `<p class="${MUTED}">Nothing at <code class="font-mono text-xs">families/${escapeHTML(result?.family_id)}/user_recipes/${escapeHTML(result?.recipe_id)}</code>. It is still in the recipes summary, so the summary and the recipes disagree.</p>`;
  }
  const r = result.recipe || {};
  const image = safeUrl(r.thumbnail_url) || safeUrl(r.image_url);
  const badges = [
    TYPE_BADGES[r.recipe_type] || (r.recipe_type ? `<span class="${BADGE.gray}">${escapeHTML(r.recipe_type)}</span>` : ""),
    r.is_shared ? `<span class="${BADGE.blue}">Shared</span>` : ""
  ].join(" ");
  // Pretty JSON is text in a <pre>, escaped like any other value.
  const json = JSON.stringify(r, null, 2);
  return `
    <div class="flex flex-col gap-6">
      <div class="flex gap-4">
        ${image ? `<img src="${escapeHTML(image)}" alt="" class="size-20 flex-none rounded-md bg-gray-100 object-cover dark:bg-gray-700">` : ""}
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap gap-1.5">${badges}</div>
          <p class="${MONO} mt-2 break-all">${escapeHTML(result.path)}</p>
        </div>
        ${isEditable(r) ? `<div><button type="button" data-family-recipe-edit class="${BUTTON.smallPrimary}">${solidIcon("pencil-square", "size-4")}Edit</button></div>` : ""}
      </div>
      ${adminEditsHTML(result.admin_edits)}
      ${factsHTML(r, members)}
      <section>
        <h4 class="${H}">Ingredients</h4>
        <div class="mt-2">${ingredientsHTML(r)}</div>
      </section>
      <section>
        <h4 class="${H}">Steps</h4>
        <div class="mt-2">${stepsHTML(r)}</div>
      </section>
      <section>
        <h4 class="${H}">Nutrition</h4>
        <div class="mt-1">${nutritionHTML(r)}</div>
      </section>
      <section>
        <div class="flex items-center justify-between gap-3">
          <h4 class="${H}">The whole document</h4>
          <button type="button" data-family-recipe-copy class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Copy JSON</button>
        </div>
        <pre data-family-recipe-json class="mt-2 max-h-96 overflow-auto rounded-md bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">${escapeHTML(json)}</pre>
      </section>
    </div>`;
}

// ---------------------------------------------------------------- editing (#1843)

const SMART_VARIANTS = VARIANTS.map(([key]) => key);
// FIELD.inputSm is full width; a step amount sits beside its unit and name.
const CALLOUT_INPUT = FIELD.inputSm.replace("block w-full", "inline-block w-20");

function variantLabel(key) {
  return (VARIANTS.find(([k]) => k === key) || [key, key])[1];
}

/** The versions the server edits: those of SMART_VARIANTS the recipe has steps for. */
function editedVariants(r) {
  const instructions = r.instructions && typeof r.instructions === "object" && !Array.isArray(r.instructions) ? r.instructions : {};
  return SMART_VARIANTS.filter((key) => Array.isArray(instructions[key]?.steps));
}

function ingredientName(i) {
  return i.name_singular || i.display_name || i.name || i.ingredient_id;
}

/** The servings a recipe's amounts are stored for (#2514), read as functions/household_servings.js reads them. */
function storedServings(r) {
  const metrics = r.planning_metrics || {};
  return [metrics.base_servings_adults, metrics.servings, r.servings, r.base_servings_adults].find((v) => typeof v === "number" && v > 0) || 2;
}

/** The editor for a recipe, in the drawer's body. Amounts are as stored: for the recipe's servings, in the ingredient's unit. */
export function editorHTML(r) {
  const head = `
    <div>
      <label for="familyRecipeEditTitle" class="${FIELD.labelSm}">Title</label>
      <input id="familyRecipeEditTitle" data-edit-title type="text" maxlength="200" value="${escapeHTML(r.title || "")}" class="${FIELD.inputSm} mt-1">
    </div>`;
  const foot = `
    <div>
      <label for="familyRecipeEditNote" class="${FIELD.labelSm}">Why (kept with the edit)</label>
      <input id="familyRecipeEditNote" data-edit-note type="text" maxlength="500" placeholder="e.g. Customer said the salt was 5 tbsp, should be 1 tsp" class="${FIELD.inputSm} mt-1">
    </div>
    <p data-edit-error class="hidden text-sm text-red-700 dark:text-red-400"></p>
    <div class="flex gap-3">
      <button type="button" data-family-recipe-save class="${BUTTON.smallPrimary}">Save for the household</button>
      <button type="button" data-family-recipe-cancel class="${BUTTON.small}">Cancel</button>
    </div>
    <p class="text-xs text-gray-500 dark:text-gray-400">Saved with the same checks as the apps' editor. The household sees it the next time the recipe loads; nobody is notified.</p>`;

  if (r.recipe_type === "raw_user") {
    const lines = (v) => escapeHTML(textLines(v).join("\n"));
    return `
      <div data-recipe-editor="raw" class="flex flex-col gap-5">
        ${head}
        <div>
          <label for="familyRecipeEditRawIngredients" class="${FIELD.labelSm}">Ingredients, one per line</label>
          <textarea id="familyRecipeEditRawIngredients" data-edit-raw-ingredients rows="8" class="${FIELD.inputSm} mt-1 font-mono">${lines(r.ingredients_text_list || r.raw_ingredients)}</textarea>
        </div>
        <div>
          <label for="familyRecipeEditRawSteps" class="${FIELD.labelSm}">Steps, one per line</label>
          <textarea id="familyRecipeEditRawSteps" data-edit-raw-steps rows="8" class="${FIELD.inputSm} mt-1">${lines(r.instructions_text_list || r.raw_instructions)}</textarea>
        </div>
        <p class="${MUTED}">Changing the ingredient lines clears what the app understood of them, as an edit in the app does.</p>
        ${foot}
      </div>`;
  }

  const ingredients = Array.isArray(r.ingredients) ? r.ingredients : [];
  const names = new Map(ingredients.map((i) => [i.ingredient_id, ingredientName(i)]));
  const ingredientRows = ingredients.map((i, n) => `
    <tr data-edit-ingredient="${n}" data-original-id="${escapeHTML(i.ingredient_id)}" data-original-amount="${escapeHTML(i.amount)}">
      <td class="${TD} w-28"><input type="number" step="any" min="0" data-edit-amount value="${escapeHTML(i.amount)}" aria-label="Amount of ${escapeHTML(ingredientName(i))}" class="${FIELD.inputSm}"></td>
      <td class="${TD} whitespace-nowrap">${escapeHTML(i.unit || "")}</td>
      <td class="${TD}">
        <span class="font-medium text-gray-900 dark:text-white">${escapeHTML(ingredientName(i))}</span>
        ${i.is_custom
          ? `<div class="${MONO} mt-0.5">${escapeHTML(i.ingredient_id)} (their own ingredient)</div><input type="hidden" data-edit-id value="${escapeHTML(i.ingredient_id)}">`
          : `<input type="text" data-edit-id value="${escapeHTML(i.ingredient_id)}" aria-label="Catalog id of ${escapeHTML(ingredientName(i))}" class="${FIELD.inputSm} mt-1 font-mono text-xs">`}
      </td>
      <td class="${TD} whitespace-nowrap"><label class="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" data-edit-remove class="rounded-sm border-gray-300">Remove</label></td>
    </tr>`).join("");

  const variants = editedVariants(r).map((key) => {
    const steps = r.instructions[key].steps.map((step, n) => {
      const callouts = (Array.isArray(step.ingredients_callouts) ? step.ingredients_callouts : []).map((c, m) => `
        <label class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
          <input type="number" step="any" min="0" data-edit-callout="${m}" data-callout-id="${escapeHTML(c.ingredient_id)}" data-original-amount="${escapeHTML(c.amount)}" value="${escapeHTML(c.amount)}" class="${CALLOUT_INPUT}">
          ${escapeHTML(c.unit || "")} ${escapeHTML(names.get(c.ingredient_id) || c.name || c.ingredient_id)}
        </label>`).join("");
      return `
        <li data-edit-step="${n}" class="flex gap-3">
          <span class="w-5 flex-none pt-1 text-right font-mono text-xs text-gray-400">${n + 1}</span>
          <div class="min-w-0 flex-1">
            <textarea data-edit-instruction rows="2" aria-label="${escapeHTML(variantLabel(key))} step ${n + 1}" class="${FIELD.inputSm}">${escapeHTML(step.instruction || "")}</textarea>
            ${callouts ? `<div class="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">${callouts}</div>` : ""}
          </div>
        </li>`;
    }).join("");
    return `
      <div data-edit-variant="${escapeHTML(key)}">
        <h5 class="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">${escapeHTML(variantLabel(key))} <span class="${MONO} normal-case">${escapeHTML(key)}</span></h5>
        <ol class="mt-2 space-y-3">${steps}</ol>
      </div>`;
  }).join("");

  return `
    <div data-recipe-editor="smart" class="flex flex-col gap-5">
      ${head}
      <section>
        <h4 class="${H}">Ingredients <span class="font-normal text-gray-500 dark:text-gray-400">for ${storedServings(r)} servings, in the stored unit</span></h4>
        <p class="mt-1 ${MUTED}">Change the catalog id to swap the match (it must be in the catalog); the note and optional flag stay.</p>
        <div class="relative mt-2 overflow-x-auto"><table class="${TABLE.table}"><tbody class="${TABLE.tbody}">${ingredientRows}</tbody></table></div>
        <label class="mt-2 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" data-edit-follow checked class="rounded-sm border-gray-300">Step amounts follow a changed amount, in every version (as the apps do)</label>
      </section>
      <section>
        <h4 class="${H}">Steps</h4>
        <p class="mt-1 ${MUTED}">Every version is shown. The amounts an ingredient's steps use must not add up to more than the ingredient in Fastest and Fewest dishes.</p>
        <div class="mt-3 space-y-5">${variants || `<p class="${MUTED}">No steps to edit.</p>`}</div>
      </section>
      ${foot}
    </div>`;
}

/** What the editor holds, read from its fields; the shape editRequest takes. */
export function readEditor(root) {
  const value = (sel) => root.querySelector(sel)?.value ?? "";
  const state = { kind: root.querySelector("[data-recipe-editor]")?.dataset.recipeEditor, title: value("[data-edit-title]"), note: value("[data-edit-note]") };
  if (state.kind === "raw") {
    state.raw_ingredients = value("[data-edit-raw-ingredients]").split("\n");
    state.raw_instructions = value("[data-edit-raw-steps]").split("\n");
    return state;
  }
  state.follow = Boolean(root.querySelector("[data-edit-follow]")?.checked);
  state.ingredients = [...root.querySelectorAll("[data-edit-ingredient]")].map((row) => ({
    original_id: row.dataset.originalId,
    original_amount: Number(row.dataset.originalAmount),
    ingredient_id: row.querySelector("[data-edit-id]")?.value.trim() || row.dataset.originalId,
    amount: Number(row.querySelector("[data-edit-amount]")?.value),
    removed: Boolean(row.querySelector("[data-edit-remove]")?.checked)
  }));
  state.steps = {};
  root.querySelectorAll("[data-edit-variant]").forEach((v) => {
    state.steps[v.dataset.editVariant] = [...v.querySelectorAll("[data-edit-step]")].map((step) => ({
      instruction: step.querySelector("[data-edit-instruction]")?.value ?? "",
      callouts: [...step.querySelectorAll("[data-edit-callout]")].map((c) => ({
        ingredient_id: c.dataset.calloutId,
        original_amount: Number(c.dataset.originalAmount),
        amount: Number(c.value)
      }))
    }));
  });
  return state;
}

/**
 * adminSaveRecipeEdit's request for an edit of `result` (an adminGetFamilyRecipe result) as `state` holds it. A Smart
 * recipe: a changed id is a new catalog match (`replaces`); a removed ingredient's step amounts go; with `follow`,
 * a step amount left as it was keeps its share of a changed ingredient (R6), in every version and prep-ahead task.
 */
export function editRequest(result, state) {
  const r = result.recipe || {};
  const base = {
    familyId: result.family_id,
    recipeId: result.recipe_id,
    baseRevision: Number.isInteger(r.edit_revision) ? r.edit_revision : 0,
    title: (state.title || "").trim(),
    note: (state.note || "").trim() || null
  };
  if (state.kind === "raw") {
    return { ...base, raw_ingredients: state.raw_ingredients, raw_instructions: state.raw_instructions };
  }
  const byOriginal = new Map(state.ingredients.map((i) => [i.original_id, i]));
  const followed = (ingredientId, original, current) => {
    const ing = byOriginal.get(ingredientId);
    if (!ing || ing.removed) return null;
    let amount = current;
    if (state.follow && current === original && ing.original_amount > 0 && ing.amount !== ing.original_amount) {
      amount = (original * ing.amount) / ing.original_amount;
    }
    return { ingredient_id: ing.ingredient_id, amount };
  };
  const ingredients = state.ingredients.filter((i) => !i.removed).map((i) => {
    const edit = { ingredient_id: i.ingredient_id, amount: i.amount };
    if (i.ingredient_id !== i.original_id) edit.replaces = i.original_id;
    return edit;
  });
  const steps = {};
  Object.entries(state.steps).forEach(([variant, list]) => {
    steps[variant] = list.map((step) => ({
      instruction: step.instruction,
      callouts: step.callouts.map((c) => followed(c.ingredient_id, c.original_amount, c.amount)).filter(Boolean)
    }));
  });
  const request = { ...base, ingredients, steps };
  // Prep ahead's tasks, followed like the steps (nothing cooks from them since #2451; whether Prep ahead stays is open, #2454).
  const tasks = Array.isArray(r.prep_ahead?.tasks) ? r.prep_ahead.tasks : [];
  if (tasks.length) {
    request.prep_ahead_tasks = tasks.map((task) => ({
      callouts: (Array.isArray(task.ingredients_callouts) ? task.ingredients_callouts : [])
        .map((c) => followed(c.ingredient_id, c.amount, c.amount))
        .filter(Boolean)
    }));
  }
  return request;
}

const PROBLEM_TEXT = { over: "the steps use more than the recipe has", unknown: "a step names an ingredient the recipe does not list", left: "some is left over", unused: "no step uses it" };

/** A refused save, said plainly: the check's problems one per line, or the server's message. */
export function saveErrorText(err) {
  const details = err?.details || {};
  if (details.reason === "conflict") {
    return `The household saved this recipe meanwhile (now revision ${details.edit_revision}). Close and open it again, then redo the edit.`;
  }
  if (details.reason === "checks" && Array.isArray(details.problems)) {
    return details.problems.map((p) => {
      const amounts = typeof p.used === "number" ? ` (steps use ${Math.round(p.used * 1000) / 1000}, the recipe has ${Math.round((p.amount || 0) * 1000) / 1000})` : "";
      return `${variantLabel(p.group)}: ${p.ingredient_id}: ${PROBLEM_TEXT[p.kind] || p.kind}${p.kind === "over" ? amounts : ""}.`;
    }).join("\n");
  }
  return err?.message || String(err);
}

let ctx = null;
let openId = null;
/** The open recipe: what adminGetFamilyRecipe returned, and what openFamilyRecipe was given. */
let current = null;
let boundBody = null;

const $ = (id) => document.getElementById(id);

/** Binds the drawer once, when a screen with a family card mounts. Reads nothing (platform/CLAUDE.md). */
export function bindFamilyRecipeDrawer(context) {
  ctx = context;
  const body = $("familyRecipeBody");
  // The Users and Families screens both bind it; the drawer is in the page once.
  if (!body || body === boundBody) return;
  boundBody = body;
  body.addEventListener("click", (e) => {
    if (e.target.closest("[data-family-recipe-copy]")) {
      const pre = body.querySelector("[data-family-recipe-json]");
      navigator.clipboard.writeText(pre?.textContent || "");
      ctx?.showToast?.("Copied the recipe's JSON.", "info");
    } else if (e.target.closest("[data-family-recipe-edit]") && current?.result?.found) {
      body.innerHTML = editorHTML(current.result.recipe || {});
    } else if (e.target.closest("[data-family-recipe-cancel]") && current?.result) {
      body.innerHTML = familyRecipeHTML(current.result, current.options.members);
    } else if (e.target.closest("[data-family-recipe-save]")) {
      saveEdit(body, e.target.closest("[data-family-recipe-save]"));
    }
  });
}

async function saveEdit(body, button) {
  if (!current?.result) return;
  const error = body.querySelector("[data-edit-error]");
  const request = editRequest(current.result, readEditor(body));
  const opened = current;
  button.disabled = true;
  error.classList.add("hidden");
  try {
    const { functions, httpsCallable } = ctx;
    const res = await httpsCallable(functions, "adminSaveRecipeEdit")(request);
    const warnings = res.data?.warnings || 0;
    ctx.showToast?.(`Saved for the household (revision ${res.data?.edit_revision})${warnings ? `, with ${warnings} warning${warnings === 1 ? "" : "s"} the apps also allow` : ""}.`, "success");
    opened.options.onSaved?.();
    if (current === opened) openFamilyRecipe(opened.options);
  } catch (err) {
    console.error("❌ adminSaveRecipeEdit failed:", err);
    error.textContent = saveErrorText(err);
    error.classList.remove("hidden");
    error.classList.add("whitespace-pre-line");
    button.disabled = false;
  }
}

/** Opens the drawer on one of the household's recipes and reads it. */
export async function openFamilyRecipe(options) {
  const { familyId, recipeId, title, members } = options;
  const body = $("familyRecipeBody");
  if (!ctx || !body) return;
  const key = `${familyId}/${recipeId}`;
  openId = key;
  current = null;
  $("familyRecipeTitle").textContent = title || "Recipe";
  $("familyRecipeSubtitle").textContent = recipeId;
  body.innerHTML = `<p class="${MUTED}">Reading the recipe…</p>`;
  showDialog("familyRecipeDrawer");
  try {
    const { functions, httpsCallable } = ctx;
    const res = await httpsCallable(functions, "adminGetFamilyRecipe")({ familyId, recipeId });
    if (openId !== key) return;
    if (res.data?.recipe?.title) $("familyRecipeTitle").textContent = res.data.recipe.title;
    current = { result: res.data, options };
    body.innerHTML = familyRecipeHTML(res.data, members);
  } catch (err) {
    console.error("❌ adminGetFamilyRecipe failed:", err);
    if (openId !== key) return;
    body.innerHTML = `<p class="text-sm text-red-700 dark:text-red-400">Could not read the recipe: ${escapeHTML(err.message || String(err))}</p>`;
  }
}
